import {
    FilterType,
    FilterCollectionType,
    FilterOptionType,
    ActiveSideFiltersType,
} from '@fiverr-private/listing_types';
import { EVENTS_MAPPING, FILTER, FILTER_COLLECTION_IDS, tracker } from '@fiverr-private/listing_lib';
import { ALL_SUBCATEGORY } from '../../../../../../../lib/utils/side_filters/constants';
import { filterNavigation, shouldFilterNavigate } from '../../../../../service/clientNavigation';
import { BI_SOURCES, SIMILAR_TIMEZONE_FILTER_ID, SIMILAR_TIMEZONE_QUERY_PARAM } from '../../../../../utils/constants';
import { getStandardUtcOffsetInMinutes, isSimilarTimezoneActive } from '../../../../../utils/timezoneFilter';
import { FILTERS_DRAWER_TRACKING_LOCATION } from '../FiltersDrawerContent/constants';
import { DISPLAY_TYPES } from '../../../../../config/filters';
import { getScalarFilterValue, isScalarValueFilterId } from '../../../side_filters/utils/scalarValueFilters';

interface QueryParamsChange {
    remove: string[];
    add: Record<string, string>;
}

interface ApplyFiltersOptions {
    /** null = leave as is; true/false = turn similar_timezone on/off on this Apply. */
    pendingSimilarTimezone?: boolean | null;
    /** Online now is not part of filterCollections, so Clear all needs this explicit signal. */
    shouldClearOnlineNow?: boolean;
    /** Tracking location; the drawer leaves it null. */
    location?: string | null;
}

export const applyFilters = (
    filterCollections: FilterCollectionType[],
    activeFilters: ActiveSideFiltersType,
    { pendingSimilarTimezone = null, shouldClearOnlineNow = false, location = null }: ApplyFiltersOptions = {}
) => {
    const newlyAppliedFilters = transformNewlyAppliedFilters(filterCollections, activeFilters);
    const selectedScOption = findSelectedSCOption(filterCollections);
    const filterKeysToGroups = mapFilterKeysToCollections(filterCollections);
    const activeLeafCategory = activeFilters?.leaf_category?.[0] || ALL_SUBCATEGORY;
    const wasSimilarTimezoneActive = isSimilarTimezoneActive(activeFilters);
    const turningOnSimilarTimezone = pendingSimilarTimezone === true && !wasSimilarTimezoneActive;
    const turningOffSimilarTimezone = pendingSimilarTimezone === false && wasSimilarTimezoneActive;
    const changingSimilarTimezone = turningOnSimilarTimezone || turningOffSimilarTimezone;

    if (shouldClearOnlineNow) {
        newlyAppliedFilters[FILTER.IS_SELLER_ONLINE.ID] = [];
        newlyAppliedFilters[FILTER.IS_SELLER_ONLINE.BUSINESS_ID] = [];
    }

    if (
        !changingSimilarTimezone &&
        !shouldFilterNavigate(newlyAppliedFilters, activeFilters) &&
        activeLeafCategory === (selectedScOption?.id || ALL_SUBCATEGORY)
    ) {
        return;
    }

    delete activeFilters.leaf_category;

    const utcOffsetInMinutes = String(getStandardUtcOffsetInMinutes());
    const queryParams: QueryParamsChange = {
        remove: turningOffSimilarTimezone ? [SIMILAR_TIMEZONE_QUERY_PARAM] : [],
        add: turningOnSimilarTimezone ? { [SIMILAR_TIMEZONE_QUERY_PARAM]: utcOffsetInMinutes } : {},
    };

    // similar_timezone is a URL param, so filterNavigation never reports it.
    if (changingSimilarTimezone) {
        tracker.trackFilter(turningOnSimilarTimezone ? EVENTS_MAPPING.FILTERS_APPLY : EVENTS_MAPPING.FILTER_REMOVE, {
            id: SIMILAR_TIMEZONE_FILTER_ID,
            value: utcOffsetInMinutes,
            location,
        });
    }

    if (selectedScOption) {
        handleSCFilterApply(
            newlyAppliedFilters,
            activeFilters,
            selectedScOption,
            filterKeysToGroups,
            queryParams,
            location
        );
    } else {
        filterNavigation(
            newlyAppliedFilters,
            activeFilters,
            location,
            BI_SOURCES.DRAWER,
            null,
            {},
            filterKeysToGroups,
            queryParams
        );
    }
};

const mapFilterKeysToCollections = (filterCollections: FilterCollectionType[]) =>
    filterCollections.reduce(
        (prev, { filters, topGrouping }) => ({
            ...prev,
            ...filters.reduce((prev, { id: filterId }) => ({ ...prev, [filterId]: topGrouping }), {}),
        }),
        {}
    );

const transformNewlyAppliedFilters = (
    filterCollections: FilterCollectionType[],
    activeFilters: ActiveSideFiltersType
) => {
    const appliedFilters: Record<string, string | string[]> = filterCollections
        .filter(({ id }) => id !== FILTER_COLLECTION_IDS.SUB_CATEGORY)
        .flatMap(({ filters }) => filters)
        .reduce(
            (prev, filter) => ({
                ...prev,
                [filter.id]: transformAppliedFilter(filter, activeFilters),
            }),
            {}
        );

    // TODO to a config with separate util to handle languages pairs
    const languagesPairFrom = appliedFilters.languages_pair_from?.[0] ?? '*';
    const languagesPairTo = appliedFilters.languages_pair_to?.[0] ?? '*';

    delete appliedFilters.languages_pair_from;
    delete appliedFilters.languages_pair_to;
    const languagesPair = `${languagesPairFrom}-${languagesPairTo}`;

    if (languagesPair !== '*-*') {
        appliedFilters.languages_pair = [languagesPair];
    } else {
        appliedFilters.languages_pair = [];
    }

    return appliedFilters;
};
const handleSCFilterApply = (
    newlyAppliedFilters: ActiveSideFiltersType,
    activeFilters: ActiveSideFiltersType,
    selectedSCOption: FilterOptionType,
    filterKeysToGroups: Record<string, string>,
    queryParams: QueryParamsChange,
    location: string | null
) => {
    const { params, alias } = selectedSCOption;
    const { sub_category: subCategory, nested_sub_category: nestedSubCategory } = params || {};
    const leafCategory = nestedSubCategory || subCategory;

    const payload = {
        location: location ?? FILTERS_DRAWER_TRACKING_LOCATION,
        alias,
        id: leafCategory,
    };

    tracker.trackLeafCategoryFilter(EVENTS_MAPPING.FILTERS_APPLY, payload);
    filterNavigation(
        newlyAppliedFilters,
        activeFilters,
        location,
        BI_SOURCES.DRAWER,
        null,
        params,
        filterKeysToGroups,
        queryParams
    );
};
const findSelectedSCOption = (filterCollections: FilterCollectionType[]) => {
    const scCollection = filterCollections.find(({ id }) => id === FILTER_COLLECTION_IDS.SUB_CATEGORY);
    const scFilter = scCollection?.filters[0] || { options: [] };
    const selectedScOption = scFilter.options.find(({ selected }) => selected);

    return selectedScOption;
};
const transformAppliedFilter = (filter: FilterType, activeFilters: ActiveSideFiltersType) => {
    const { options, display_type: displayType, id } = filter;

    if (isScalarValueFilterId(id)) {
        const hasSelected = options.some(({ selected }) => selected);

        if (!hasSelected) {
            return null;
        }

        // Keep an already active value (e.g. PRO.SELECTED_VALUE_ONLY) rather than widening it.
        const currentValue = activeFilters?.[id];

        return typeof currentValue === 'string' ? currentValue : getScalarFilterValue(id);
    }

    if (displayType === DISPLAY_TYPES.INPUT_RANGE) {
        const [{ selected, max }] = options;

        if (selected) {
            if (!max) {
                return null;
            }

            return [0, max];
        }

        return null;
    }

    return options.filter(({ selected, id }) => selected && id).map(({ id }) => id);
};
